import axios from 'axios';

// Get the base URL from environment variable
const rawApiUrlFromEnv = process.env.REACT_APP_API_URL ;
const API_BASE_URL = rawApiUrlFromEnv || 'http://localhost:8000';
// --- BEGIN DEBUG LOGS ---
console.log('[API Service] Raw REACT_APP_API_URL from process.env:', rawApiUrlFromEnv);
console.log('[API Service] API_BASE_URL that will be used by Axios:', API_BASE_URL);
if (!rawApiUrlFromEnv) {
  console.warn('[API Service] REACT_APP_API_URL is not set! Falling back to default:', API_BASE_URL);
}
// --- END DEBUG L
// Create axios instance with complete base URL including the /api/v1 path
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Document management API calls
export const documentApi = {
  // Get all topics
  getTopics: async () => {
    try {
      const response = await apiClient.get('/documents');
      return response.data;
    } catch (error) {
      console.error('Error fetching topics:', error);
      throw error;
    }
  },

  // Get documents for a specific topic
  getDocumentsByTopic: async (topicId) => {
    try {
      const response = await apiClient.get(`/documents/${topicId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for topic ${topicId}:`, error);
      throw error;
    }
  },

  // Upload a document
  uploadDocument: async (formData) => {
    try {
      const response = await apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },
};

// --- BEGIN DEBUG FOR API CLIENT ---
console.log('[API Service] Axios apiClient instance created with baseURL:', apiClient.defaults.baseURL);
// --- END DEBUG FOR API CLIENT ---

// Query API calls
export const queryApi = {
  // Simple query
  query: async (queryData) => {
    try {
      const response = await apiClient.post('/query', queryData);
      return response.data;
    } catch (error) {
      console.error('Error executing query:', error);
      throw error;
    }
  },

  // Retrieve chunks without generating answer
  retrieve: async (queryData) => {
    try {
      const response = await apiClient.post('/retrieve', queryData);
      return response.data;
    } catch (error) {
      console.error('Error retrieving chunks:', error);
      throw error;
    }
  },

  // Query with reasoning
  queryWithReasoning: async (queryData) => {
    // --- BEGIN DEBUG FOR SPECIFIC CALL ---
    console.log('[API Service] queryWithReasoning called. Current apiClient baseURL:', apiClient.defaults.baseURL);
    // --- END DEBUG FOR SPECIFIC CALL ---
    try {
      const response = await apiClient.post('/extract_reasoning', queryData);
      return response.data;
    } catch (error) {
      console.error('Error executing query with reasoning:', error);
      throw error;
    }
  },

  // Bulk query
  bulkQuery: async (bulkQueryData) => {
    try {
      const response = await apiClient.post('/bulk_query', bulkQueryData);
      return response.data;
    } catch (error) {
      console.error('Error executing bulk query:', error);
      throw error;
    }
  },

  // Flexible query tool
  queryFlexibleTool: async (queryData) => { // queryData is expected to be FormData here
    try {
      // When sending FormData, Axios will typically set the Content-Type
      // to 'multipart/form-data' automatically.
      // However, if we need to be explicit or override defaults:
      const config = {};
      if (queryData instanceof FormData) {
        // To ensure Axios sets the 'Content-Type' to 'multipart/form-data'
        // with the correct boundary, we should remove any default 'Content-Type'
        // header for this specific request. Setting it to undefined or null
        // in the request config usually achieves this.
        config.headers = {
          'Content-Type': undefined, // Let Axios handle it for FormData
        };
        // Alternatively, some prefer to explicitly set it, but letting Axios do it is safer for boundaries.
        // config.headers = { 'Content-Type': 'multipart/form-data' }; // Less safe for boundary
        
        const response = await apiClient.post('/query_flexible_tool', queryData, config);
        return response.data;

      } else {
        // Fallback for non-FormData, though current usage sends FormData
        console.warn('[API Service] queryFlexibleTool called with non-FormData. This might be deprecated.');
        const response = await apiClient.post('/query_flexible_tool', queryData);
        return response.data;
      }
    } catch (error) {
      console.error('Error executing flexible query:', error);
      throw error;
    }
  },

  // Orchestrated search - runs all services in parallel
  orchestrateSearch: async (searchData) => {
    try {
      const response = await apiClient.post('/search/orchestrate', searchData);
      return response.data;
    } catch (error) {
      console.error('Error executing orchestrated search:', error);
      throw error;
    }
  },

  // Synthesize selected results into final answer
  synthesizeAnswer: async (synthesisData) => {
    try {
      const response = await apiClient.post('/answer/synthesize', synthesisData);
      return response.data;
    } catch (error) {
      console.error('Error synthesizing answer:', error);
      throw error;
    }
  },
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await axios.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking API health:', error);
    throw error;
  }
};

// Interactive Reasoning API calls
export const reasoningApi = {
  // Generate reasoning plan
  generatePlan: async (planData) => {
    try {
      const response = await apiClient.post('/plan/generate', planData);
      return response.data;
    } catch (error) {
      console.error('Error generating reasoning plan:', error);
      throw error;
    }
  },

  // Execute reasoning plan
  executePlan: async (executionData) => {
    try {
      const response = await apiClient.post('/plan/execute', executionData);
      return response.data;
    } catch (error) {
      console.error('Error executing reasoning plan:', error);
      throw error;
    }
  },

  // Create step from type
  createStep: async (stepData) => {
    try {
      const response = await apiClient.post('/plan/create-step', stepData);
      return response.data;
    } catch (error) {
      console.error('Error creating reasoning step:', error);
      throw error;
    }
  },

  // Get execution status
  getExecutionStatus: async (executionId) => {
    try {
      const response = await apiClient.get(`/plan/status/${executionId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting execution status:', error);
      throw error;
    }
  },

  // Get step types
  getStepTypes: async () => {
    try {
      const response = await apiClient.get('/plan/step-types');
      return response.data;
    } catch (error) {
      console.error('Error getting step types:', error);
      throw error;
    }
  },
};

// Flow management API calls
export const flowApi = {
  // Get flow recommendations
  getRecommendations: async (recommendationData) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiClient.post('/flows/recommend', recommendationData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting flow recommendations:', error);
      throw error;
    }
  },

  // Get flow by ID
  getFlow: async (flowId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiClient.get(`/flows/${flowId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting flow:', error);
      throw error;
    }
  },

  // Record flow usage
  recordUsage: async (flowId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiClient.post(`/flows/${flowId}/use`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error recording flow usage:', error);
      throw error;
    }
  },

  // Get user's flows
  getMyFlows: async (params) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiClient.get('/flows/my-flows', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting my flows:', error);
      throw error;
    }
  },
};

const apiService = {
  documentApi,
  queryApi,
  reasoningApi,
  flowApi,
  healthCheck,
};

export default apiService;
